/**
 * Top Customers Table Component
 *
 * Component for displaying top customers data in a table
 */

import React, {useState} from 'react';
import {TopCustomer} from '../models/report.model';
import AppTable from '../../../Presentation/components/ui/AppTable';
import {AppText, AppTableText} from '../../../Presentation/components/ui/Typography';
import {TableColumn} from '../../../Presentation/components/ui/AppTable';
import {formatCurrency} from '../../../Presentation/utils/numberUtils';

interface TopCustomersTableProps {
  customers: TopCustomer[];
}

export const TopCustomersTable: React.FC<TopCustomersTableProps> = ({customers}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Calculate pagination
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = customers.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  const columns: TableColumn<TopCustomer>[] = [
    {
      key: 'customer_name',
      title: 'اسم العميل',
      render: (customer) => <AppText>{customer.customer_name}</AppText>
    },
    {
      key: 'total_amount',
      title: 'إجمالي المبلغ',
      render: (customer) => <AppText>{formatCurrency(customer.total_amount)}</AppText>
    },
    {
      key: 'paid_amount',
      title: 'المدفوع',
      render: (customer) => <AppText>{formatCurrency(customer.paid_amount)}</AppText>
    },
    {
      key: 'remaining',
      title: 'المتبقي',
      render: (customer) => {
        const remaining = customer.total_amount - customer.paid_amount;
        return <AppText>{formatCurrency(remaining)}</AppText>;
      }
    },
    {
      key: 'payment_rate',
      title: 'معدل الدفع',
      render: (customer) => {
        const paymentRate = customer.total_amount > 0
          ? (customer.paid_amount / customer.total_amount) * 100
          : 0;

        return (
          <div className="flex items-center">
            <div className="flex-1 bg-gray-200 rounded-full h-2 ml-3">
              <div
                className="bg-brand-600 h-2 rounded-full"
                style={{width: `${Math.min(paymentRate, 100)}%`}}
              ></div>
            </div>
            <AppTableText className="text-gray-600 w-12 text-left">
              {paymentRate.toFixed(1)}%
            </AppTableText>
          </div>
        );
      }
    }
  ];

  return (
    <AppTable
      columns={columns}
      data={paginatedData}
      rowKey={(_, index) => index?.toString() || '0'}
      emptyText="لا توجد بيانات عملاء متاحة"
      hidePagination={false}
      page={currentPage}
      pageSize={pageSize}
      totalCount={customers.length}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
    />
  );
};
